import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, MessageSquare, Phone, Globe, QrCode, Mail, Link } from 'lucide-react';
import { Card } from '@/components/ui/card';
import HomeNav from '../HomeNav';
import HomeFooter from '../HomeFooter';
import GenieEmbed from '@/components/widget/GenieEmbed';
import CategoryCard from '@/pages/WhatCanGeniesDo/components/CategoryCard';
import OnboardingTourCards from '@/pages/Dashboard/components/OnboardingTourCards';
import { GENIE_CAPABILITY_CATEGORIES } from '@/data/genie/categories';
import { SideDrawer } from '@/components/common/SideDrawer';

const WhatCanGeniesDoPublic = () => {
  const navigate = useNavigate();
  const [openSetup, setOpenSetup] = useState(false);

  const deploymentChannels = [
    { icon: Globe, title: "Website Embed", description: "Add to any website with a simple snippet" },
    { icon: QrCode, title: "QR Codes", description: "Perfect for physical locations" },
    { icon: Link, title: "Direct Links", description: "Share dedicated URLs" },
    { icon: Phone, title: "Phone Numbers", description: "Connect to phone lines" },
    { icon: Mail, title: "Email Integration", description: "Automated follow-ups" },
    { icon: MessageSquare, title: "Live Chat", description: "Real-time conversations" }
  ];

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F9FAFC]">
      <HomeNav />

      <main className="flex-1 w-full">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center py-20 px-4 sm:px-6 lg:px-8">
          {/* Background Image with overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="/new-imagery/contentlead-image-c10b3f29-a5df-4c55-b9cb-ee6f8fdf76b6.png"
              alt="What Can Genies Do Background"
              className="w-full h-full object-cover"
            />
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full mb-6">
              <Sparkles className="h-4 w-4 text-white" />
              <span className="text-sm font-medium text-white">Discover Genie Capabilities</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white mb-6 drop-shadow-2xl">
              What Can Genies Do?
            </h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-8 drop-shadow-lg">
              From customer support to sales, internal operations to creative experiences:
              explore the endless possibilities of AI-powered voice agents
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/signup')}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg"
              >
                Create Your First Genie
                <ArrowRight className="ml-2 h-5 w-5 inline" />
              </button>
              <button
                onClick={() => setOpenSetup(true)}
                className="px-8 py-3 bg-white text-gray-900 hover:bg-gray-50 font-semibold rounded-lg border-2 border-gray-200 transition-all duration-300 text-lg"
              >
                See How It Works
              </button>
            </div>
          </div>
        </section>

        {/* Interactive Categories Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Try Live Examples
              </h2>
              <p className="text-lg text-gray-600">
                Click on any Genie avatar below to experience a live conversation
              </p>
            </div>

            <div className="space-y-6">
              {GENIE_CAPABILITY_CATEGORIES.map((category) => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  onAction={() => navigate("/signup")}
                />
              ))}
            </div>
          </div>
        </section>

        {/* How to Set Up Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-blue-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Set Up in 5 Simple Steps
              </h2>
              <p className="text-lg text-gray-600">
                Get your Genie up and running in minutes
              </p>
            </div>

            <OnboardingTourCards
              layout="horizontal"
              emphasis="large"
              numberPosition="top"
              showHeaderTitle={false}
            />
          </div>
        </section>

        {/* Deployment Channels */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Deploy Everywhere
              </h2>
              <p className="text-lg text-gray-600">
                Meet your customers wherever they are
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {deploymentChannels.map((channel, index) => {
                const Icon = channel.icon;
                return (
                  <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {channel.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {channel.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Key Features Grid */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Powerful Features Built-In
              </h2>
              <p className="text-lg text-gray-600">
                Enterprise-grade capabilities in every Genie
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Voice Intelligence
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Natural conversation flow with context retention</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">40+ languages supported</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Advanced voice recognition and synthesis</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Complete Customization
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Full brand customization and personality tuning</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Goal-driven conversations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Custom knowledge base training</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Lead Capture & Analytics
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Progressive profiling and custom forms</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Conversation insights and sentiment analysis</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Real-time performance metrics</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Enterprise Security
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Bank-grade security and encryption</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">GDPR compliant with data privacy controls</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-3 mt-1.5 flex-shrink-0" />
                    <span className="text-gray-700">Access management and audit logs</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Customer Experience?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join thousands of businesses using Genies to deliver exceptional service 24/7
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/signup')}
                className="px-8 py-3 bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 text-lg"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5 inline" />
              </button>
              <button
                onClick={() => navigate('/live-agent/business-onboarding')}
                className="px-8 py-3 text-white border-2 border-white hover:bg-white/10 font-semibold rounded-lg transition-all duration-300 text-lg"
              >
                Try a Demo
              </button>
            </div>
          </div>
        </section>
      </main>

      <HomeFooter />
      <GenieEmbed position="bottom-right" />

      {/* Setup Steps Drawer */}
      <SideDrawer
        isOpen={openSetup}
        onClose={() => setOpenSetup(false)}
        width="720px"
        title="How to Set Up Your Genie"
      >
        <OnboardingTourCards
          layout="vertical"
          emphasis="large"
          numberPosition="left"
          showHeaderTitle={false}
        />
      </SideDrawer>
    </div>
  );
};

export default WhatCanGeniesDoPublic;